import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { set } from 'lodash';
import { FilterList } from '@mui/icons-material';
import { Box } from '@mui/material';

import dashboardService from '../../../service/dashboard.service';
import { DashboardTypeList } from '../../../config/dashboard';
import { AutoCompleteSelectUI, FormLabelUI, SelectUI, DatePickerUI } from '../../Interface';
import BiReport from '../BiReport/BiReport';
import useUsers from '../../../context/UsersContext';
import useParam from '../../../context/ParamContext';
import { generateYears } from '../../../utils/dateHelper';
import { months } from '../../../config/months';
import useSaveView from '../../../hooks/useSaveView';
import {
  hydrateDashboardFilter,
  loadDashboardFilter,
  restrictDashboardFilter,
  saveDashboardFilter,
  serializeDashboardFilter,
} from '../../../utils/dashboardFilterStorage';
import { sxConfig } from './dashboard.style';

const weeks = Array.from({ length: 53 }, (_, index) => index + 1);
const filterFieldWidth = 190;
const filterGap = 12;
const filterHorizontalPadding = 64;

const Dashboard = () => {
  const { dashboardId } = useParams();
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const saveView = useSaveView(user);

  const [selectedDashboard, setSelectedDashboard] = useState({});
  const [dashboardType, setDashboardType] = useState({});
  const [yearVals, setYearVals] = useState([]);
  const [businessPartners, setBusinessPartners] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [autocompleteData, setAutocompleteData] = useState({
    businessPartners: [],
    responsibles: [],
  });

  const [filter, setFilter] = useState(() => loadDashboardFilter(dashboardId, user?.userId));
  const [refreshKey, setRefreshKey] = useState(0);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
  const [gridAvailableHeight, setGridAvailableHeight] = useState(360);
  const headerRef = useRef(null);
  const gridRef = useRef(null);
  const filterPersistenceReadyRef = useRef(false);
  const skipNextFilterPersistenceRef = useRef(false);

  const dashboardColumns = Number(dashboardType?.column) || 1;
  const reportCount = selectedDashboard?.dashboardReport?.length || 0;
  const responsiveColumnCap = viewportWidth >= 1600 ? 4 : viewportWidth >= 1200 ? 3 : viewportWidth >= 900 ? 2 : 1;
  const isDesktop = viewportWidth >= 1200;
  const gridColumns = Math.max(1, Math.min(dashboardColumns, responsiveColumnCap));
  const gridRows = Math.max(1, Math.ceil(Math.max(1, reportCount) / gridColumns));
  const shouldGridScroll = !isDesktop && gridColumns !== dashboardColumns;
  const gridGap = 8;
  const minCardHeight = shouldGridScroll ? 450 : 260;
  const availableGridHeight = Math.max(280, gridAvailableHeight);
  const cardHeight = Math.max(
    minCardHeight,
    Math.floor((availableGridHeight - (gridRows - 1) * gridGap) / gridRows) - 10
  );
  const reportHeight = Math.max(300, cardHeight - 22);
  const filterControlCount =
    [
      selectedDashboard?.bpFilter,
      selectedDashboard?.responsibleFilter,
      selectedDashboard?.yearFilter,
      selectedDashboard?.halfYearFilter,
      selectedDashboard?.quarterFilter,
      selectedDashboard?.monthFilter,
      selectedDashboard?.weekFilter,
      selectedDashboard?.intervalFilter,
    ].filter(Boolean).length + (selectedDashboard?.intervalFilter ? 1 : 0);
  const activeFilterCount = [
    filter?.businessPartner,
    filter?.responsible,
    filter?.year,
    filter?.halfYear,
    filter?.quarter,
    filter?.month,
    filter?.week,
    filter?.interval?.from,
    filter?.interval?.to,
  ].filter(Boolean).length;
  const requiredFilterWidth =
    filterControlCount * filterFieldWidth + Math.max(0, filterControlCount - 1) * filterGap + filterHorizontalPadding;
  const floatingFilterColumns = Math.max(
    1,
    Math.min(
      3,
      filterControlCount,
      Math.floor((viewportWidth - filterHorizontalPadding + filterGap) / (filterFieldWidth + filterGap))
    )
  );
  const floatingFilterPanelWidth =
    floatingFilterColumns * filterFieldWidth + Math.max(0, floatingFilterColumns - 1) * filterGap + 24;

  const getDashboard = () => {
    dashboardService.getDashboard(dashboardId).then((data) => {
      setSelectedDashboard(data);
      setFilter((prev) => restrictDashboardFilter(prev, data));
    });
  };

  const getBusinessPartners = () => {
    //removed slpcode filter 2024.10.22 CZI -> Folplast
    dashboardService.getBusinessPartners(/*user?.userSalesPerson?.slpCode*/).then((data) => {
      setBusinessPartners(data);
    });
  };

  const getResponsibles = () => {
    //@TODO responsible type if necessary
    dashboardService.getResponsibles('SALES').then((data) => {
      setResponsibles(data);
    });
  };

  const changeFilter = (key, value) => {
    setFilter((prev) => {
      const newFilter = { ...prev };
      set(newFilter, key, value);
      return newFilter;
    });
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getParam();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const recalculateGridHeight = () => {
      if (!gridRef.current) return;

      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const gridTop = gridRef.current.getBoundingClientRect().top;
      const headerBottom = headerRef.current?.getBoundingClientRect().bottom ?? 0;
      const contentTop = Math.max(gridTop, headerBottom);
      const bottomSafeOffset = 0;
      const nextHeight = Math.floor(viewportHeight - contentTop - bottomSafeOffset);
      setGridAvailableHeight(Math.max(280, nextHeight));
    };

    recalculateGridHeight();
    window.addEventListener('resize', recalculateGridHeight);
    window.visualViewport?.addEventListener('resize', recalculateGridHeight);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalculateGridHeight) : null;
    if (headerRef.current) resizeObserver?.observe(headerRef.current);

    return () => {
      window.removeEventListener('resize', recalculateGridHeight);
      window.visualViewport?.removeEventListener('resize', recalculateGridHeight);
      resizeObserver?.disconnect();
    };
  }, [gridColumns, gridRows, filterControlCount]);

  useLayoutEffect(() => {
    if (!headerRef.current || filterControlCount === 0) return undefined;

    const recalculateFilterLayout = () => {
      setFiltersCollapsed(headerRef.current.clientWidth < requiredFilterWidth);
    };
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalculateFilterLayout) : null;

    recalculateFilterLayout();
    resizeObserver?.observe(headerRef.current);
    window.addEventListener('resize', recalculateFilterLayout);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', recalculateFilterLayout);
    };
  }, [filterControlCount, requiredFilterWidth]);

  useEffect(() => {
    if (!filtersCollapsed) setFiltersOpen(false);
  }, [filtersCollapsed]);

  useEffect(() => {
    setYearVals(generateYears(params.DASHBOARFILTER_MIN_YEAR ?? 2021));
  }, [params]);

  useEffect(() => {
    if (Object.keys(user).length > 0) getBusinessPartners();
  }, [user]);

  useEffect(() => {
    if (businessPartners.length > 0) {
      const bpAutocompleteData = businessPartners.map((bp) => ({
        label: `${bp.companyId} - ${bp.companyName}`,
        value: bp.companyName,
      }));

      setAutocompleteData((prev) => {
        return { ...prev, businessPartners: bpAutocompleteData };
      });
    } else {
      setAutocompleteData((prev) => {
        return { ...prev, businessPartners: [] };
      });
    }
  }, [businessPartners]);

  useEffect(() => {
    if (responsibles.length > 0) {
      const responsiblesData = responsibles.map((responsible) => ({
        label: `${responsible.responsibleCode} - ${responsible.responsibleName}`,
        value: responsible.responsibleCode,
      }));

      setAutocompleteData((prev) => {
        return { ...prev, responsibles: responsiblesData };
      });
    } else {
      setAutocompleteData((prev) => {
        return { ...prev, responsibles: [] };
      });
    }
  }, [responsibles]);

  useEffect(() => {
    getDashboard();
    setFiltersOpen(false);

    filterPersistenceReadyRef.current = false;
    if (user?.userId) {
      const savedFilter =
        user.views?.dashboardFilter?.[dashboardId] ?? loadDashboardFilter(dashboardId, user.userId);
      skipNextFilterPersistenceRef.current = true;
      setFilter(hydrateDashboardFilter(savedFilter));
      filterPersistenceReadyRef.current = true;
    }
  }, [dashboardId, user?.userId]);

  useEffect(() => {
    if (!filterPersistenceReadyRef.current || !user?.userId) return undefined;

    if (skipNextFilterPersistenceRef.current) {
      skipNextFilterPersistenceRef.current = false;
      return undefined;
    }

    const serializedFilter = serializeDashboardFilter(filter);
    saveDashboardFilter(dashboardId, user.userId, serializedFilter);
    saveView('dashboardFilter', { [dashboardId]: serializedFilter });
    return undefined;
  }, [dashboardId, filter, user?.userId]);

  useEffect(() => {
    const refreshMin = Number(selectedDashboard?.refreshMin);

    if (!Number.isFinite(refreshMin) || refreshMin <= 0) return undefined;

    const intervalId = setInterval(() => {
      getDashboard();
      setRefreshKey((prev) => prev + 1);
    }, refreshMin * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [dashboardId, selectedDashboard?.refreshMin]);

  useEffect(() => {
    if (Object.keys(selectedDashboard).length > 0) {
      const findedDashboardType = DashboardTypeList.find((dt) => dt.value === selectedDashboard.type);
      setDashboardType(findedDashboardType);

      if (selectedDashboard.responsibleFilter) getResponsibles();
    }
  }, [selectedDashboard]);

  return (
    <>
      {filterControlCount > 0 && (
        <div
          ref={headerRef}
          className="sticky top-0 lg:top-16 z-30 flex justify-end bg-gray-100 px-4 pt-1 pb-0.5"
        >
          {filtersCollapsed && filtersOpen && (
            <button
              type="button"
              aria-label="Szűrőpanel bezárása"
              tabIndex={-1}
              className="fixed inset-0 z-40 cursor-default border-0 bg-transparent p-0"
              onClick={() => setFiltersOpen(false)}
            />
          )}
          <div className="relative z-50 w-fit max-w-full rounded-xl border border-blue-100 bg-headerToolbar px-3 py-1 shadow-sm">
          {filtersCollapsed && (
            <div className="flex justify-end">
              <button
                type="button"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen((prev) => !prev)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-labelColor shadow-sm transition-colors hover:bg-gray-50"
              >
                <FilterList sx={{ color: '#0076f8', fontSize: 16 }} />
                Szűrők ({activeFilterCount})
              </button>
            </div>
          )}
          {(!filtersCollapsed || filtersOpen) && (
            <Box
              className={`grid gap-x-3 gap-y-1 ${
                filtersCollapsed
                  ? 'absolute right-0 top-full z-50 mt-1 max-h-[calc(40vh-40px)] overflow-y-auto rounded-xl border border-blue-100 bg-headerToolbar p-3 shadow-xl'
                  : ''
              }`}
              sx={sxConfig.fieldColor}
              style={{
                ...(filtersCollapsed && {
                  width: `${Math.min(floatingFilterPanelWidth, viewportWidth - 32)}px`,
                }),
                gridTemplateColumns: filtersCollapsed
                  ? `repeat(${floatingFilterColumns}, minmax(0, 190px))`
                  : `repeat(${filterControlCount}, 190px)`,
                justifyContent: 'end',
              }}
            >
          {selectedDashboard?.bpFilter && (
            <div className="col-span-1 max-w-[300px]">
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Üp." />}
                variant="standard"
                onChange={(_e, newVal) => {
                  changeFilter('businessPartner', newVal);
                }}
                value={filter?.businessPartner ?? null}
                dataset={autocompleteData.businessPartners}
                autoFocus
                limitTags={1}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}

          {selectedDashboard?.responsibleFilter && (
            <div className="col-span-1">
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Felelős" />}
                variant="standard"
                onChange={(_e, newVal) => {
                  changeFilter('responsible', newVal ?? null);
                }}
                value={filter?.responsible}
                dataset={autocompleteData.responsibles}
                autoFocus
                limitTags={1}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}

          {selectedDashboard?.yearFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Év" />}
                option={yearVals}
                value={filter.year}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('year', value);
                }}
                onClear={() => {
                  changeFilter('year', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.quarterFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Negyedév" />}
                option={[1, 2, 3, 4]}
                value={filter.quarter}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('quarter', value);
                }}
                onClear={() => {
                  changeFilter('quarter', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.halfYearFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Félév" />}
                option={[1, 2]}
                value={filter.halfYear}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('halfYear', value);
                }}
                onClear={() => {
                  changeFilter('halfYear', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.monthFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Hónap" />}
                option={months}
                value={filter.month}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('month', value);
                }}
                onClear={() => {
                  changeFilter('month', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.weekFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Hét" />}
                option={weeks}
                value={filter.week}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('week', value);
                }}
                onClear={() => {
                  changeFilter('week', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.intervalFilter && (
            <>
              <div className="col-span-1">
                <DatePickerUI
                  label={<FormLabelUI text="Kezdő dátum" />}
                  value={filter.interval.from}
                  onChange={(newDate) => {
                    changeFilter('interval.from', newDate ? newDate.startOf('day') : null);
                  }}
                  sx={sxConfig.fieldColor}
                />
              </div>
              <div className="col-span-1">
                <DatePickerUI
                  label={<FormLabelUI text="Vége dátum" />}
                  value={filter.interval.to}
                  onChange={(newDate) => {
                    changeFilter('interval.to', newDate ? newDate.startOf('day') : null);
                  }}
                  sx={sxConfig.fieldColor}
                />
              </div>
            </>
          )}
            </Box>
          )}
          </div>
        </div>
      )}
      <div
        ref={gridRef}
        className="grid overflow-x-hidden px-4 bg-gray-100 pt-0 pb-2"
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
          height: `${availableGridHeight}px`,
          gap: `${gridGap}px`,
          overflowY: shouldGridScroll ? 'auto' : 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {selectedDashboard?.dashboardReport?.map((report, index) => {
          return (
            <div
              className="col-span-1 overflow-hidden bg-white shadow sm:rounded-lg "
              style={{ height: `${cardHeight}px` }}
              key={`${report.biReportId}-${index}`}
            >
              <BiReport
                reportId={report.biReportId}
                height={reportHeight}
                filter={filter}
                dashboardData={selectedDashboard}
                reRender={refreshKey}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Dashboard;
